<template>
    <DCard padding>
        <DRow class="mb-3">
            <DCol sm="2">
                <DSelect
                    id="status-dropdown"
                    v-model="filterData.status"
                    :options="statusOptions"
                    :label="__('contracts.pricings.filters.status')"
                />
            </DCol>
            <DCol sm="2">
                <DSelect
                    id="mail-status-dropdown"
                    v-model="filterData.mailStatus"
                    :options="mailStatusOptions"
                    :label="__('contracts.pricings.filters.mail_status')"
                />
            </DCol>
            <DCol
                sm="3"
                class="d-flex align-items-end gap-2"
            >
                <DButton
                    id="filter-apply"
                    :label="__('messages.actions.filter')"
                    @click="changePage"
                />
                <DButton
                    id="filter-reset"
                    :label="__('messages.actions.reset_filter')"
                    @click="resetFilters"
                />
            </DCol>
        </DRow>
        <DRow>
            <DCol>
                <DTable
                    :items="items"
                    :fields="fields"
                    hover
                >
                    <template #contract="{item}">
                        <td v-if="userPermissions.includes('contract.view')">
                            <Link :href="contractRoute(item)">
                                {{ item.product }} ({{ item.contract.id }})
                            </Link>
                        </td>
                        <td v-else>
                            {{ item.product }} ({{ item.contract.id }})
                        </td>
                    </template>
                    <template #customer="{item}">
                        <td v-if="userPermissions.includes('customer.view')">
                            <Link :href="customerProfileRoute(item.customer.id)">
                                {{ item.customerFullName }} ({{ item.customer.id }})
                            </Link>
                        </td>
                        <td v-else>
                            {{ item.customerFullName }}
                        </td>
                    </template>
                    <template #price="{item}">
                        <td v-if="item.price === null">
                            {{ __('contracts.pricing.not_applicable') }}
                        </td>
                        <td v-else>
                            {{ priceFormatForDisplay(item.price, 2, item.customer?.market_region_id) }}
                        </td>
                    </template>
                    <template #status="{item}">
                        <td>
                            <DTag
                                :label="__('contracts.pricings.statuses.' + item.status)"
                                :color="getStatusTagColor(item.status)"
                            />
                        </td>
                    </template>
                    <template #mailStatus="{item}">
                        <td v-if="item.mail_status === null">
                            N/A
                        </td>
                        <td v-else>
                            <DTag
                                :label="__('contracts.pricings.statuses.' + item.mail_status)"
                                :color="getStatusTagColor(item.mail_status)"
                            />
                        </td>
                    </template>
                    <template #extraordinaryTermination="{item}">
                        <td>
                            {{ item.extraordinaryTermination ? 'yes' : 'no' }}
                        </td>
                    </template>
                    <template #actions="{item}">
                        <td v-if="item.mail_status === 'missing'">
                            <DButton
                                v-c-tooltip="__('contracts.pricings.messages.resend_email')"
                                icon="mail"
                                color="danger"
                                :mini="true"
                                @click="resendCustomerEmail(item)"
                            />
                        </td>
                        <td v-if="item.status === 'draft'">
                            <DButton
                                icon="eye"
                                mini
                                @click="openModal(item)"
                            />
                        </td>
                    </template>
                </DTable>
                <DPagination
                    v-if="paginationData.last_page >= 1"
                    :active-page="paginationData.current_page"
                    :pages="paginationData.last_page"
                    align="center"
                    @update:activePage="changePage"
                />
            </DCol>
        </DRow>
        <DFabs>
            <DFab
                v-if="items.length > 0 && items[0].status === 'draft'"
                id="btn-apply-pricing"
                icon="download"
                color="secondary"
                :label="__('contracts.pricing.messages.domain_decoupling_csv')"
                @click="downloadDomainDecouplingCsv()"
            />
            <DFab
                v-if="items.length > 0 && items[0].status === 'draft'"
                id="btn-apply-pricing"
                icon="content-save"
                color="primary"
                :label="__('contracts.pricing.messages.apply')"
                @click="submitPricings"
            />
            <DFab
                v-if="items.length > 0 && items[0].status === 'draft'"
                id="btn-show-cancel-pricing"
                icon="close"
                color="danger"
                :label="__('contracts.pricing.messages.abort')"
                @click="cancelPricings"
            />
            <DFab
                id="btn-preview-new-pricing"
                icon="arrow-left"
                color="primary"
                :label="__('contracts.pricing.messages.back')"
                @click="pricingsRoute"
            />
        </DFabs>
        <EmailPreviewModal
            v-if="showModal"
            :show-modal="showModal"
            :display-data="displayData"
            :errors="errors"
            :mail-template="props.pricingUpload.mail_template"
            @closeModal="closeModal"
        />
    </DCard>
</template>

<script setup>
import { route } from '~@/Composables/route'
import { router, usePage } from '@inertiajs/vue3'
import { __ } from '~@/Composables/translation'
import { computed, ref } from 'vue'
import EmailPreviewModal from '~@/Components/Contracts/Pricing/EmailPreviewModal'

const props = defineProps({
    items: {
        type: Array,
        default: () => {
            return []
        }
    },
    paginationData: {
        type: Object,
        default: () => {
            return {}
        }
    },
    pricingUpload: {
        type: Object,
        default: () => {
            return {}
        }
    },
    statusOptions: {
        type: Array,
        default: () => {
            return []
        }
    },
    mailStatusOptions: {
        type: Array,
        default: () => {
            return []
        }
    },
    filters: {
        type: Object,
        default: () => {
            return {}
        }
    }
})

const fields = [
    { key: 'id', label: __('contracts.pricings.headers.id') },
    { key: 'contract', label: __('contracts.pricings.headers.contract') },
    { key: 'customer', label: __('contracts.pricings.headers.customer') },
    { key: 'price', label: __('contracts.pricings.headers.price') },
    { key: 'accountingPeriod', label: __('contracts.pricings.headers.accountingPeriod') },
    { key: 'discountValue', label: __('contracts.pricings.headers.discountValue') },
    { key: 'discountType', label: __('contracts.pricings.headers.discountType') },
    { key: 'discountName', label: __('contracts.pricings.headers.discountName') },
    { key: 'status', label: __('contracts.pricings.headers.status') },
    { key: 'mailStatus', label: __('contracts.pricings.headers.mail_status') },
    { key: 'date', label: __('contracts.pricings.headers.date') },
    { key: 'extraordinaryTermination', label: __('contracts.pricings.headers.extraordinary_termination') },
    { key: 'announcementDate', label: __('contracts.pricings.headers.announcement_date') },
    { key: 'mail_template', label: __('contracts.pricings.headers.mail_template') },
    { key: 'actions', label: '', _style: { minWidth: '10%' } }
]
const pricingsRoute = () => router.get(route('contracts.pricing.index'))

const submitPricings = () => router.get(route('contracts.pricing.submit', props.pricingUpload.id))

const cancelPricings = () => router.get(route('contracts.pricing.cancel', props.pricingUpload.id))

const userPermissions = computed(() => usePage().props.auth.permissions)

const customerProfileRoute = (id) => route('customers.customers.show', id)
const contractRoute = (value) => {
    return route('customers.customers.contracts.show', {
        customer: value.customer.id,
        contract: value.contract.id
    })
}

const filterData = ref({
    status: props.filters.status ?? null,
    mailStatus: props.filters.mailStatus ?? null
})

const showModal = ref(false)
let displayData = null
let errors = []

const getStatusTagColor = (status) => {
    if (status === 'draft') {
        return 'primary'
    }
    if (status === 'open' || status === 'not_sent') {
        return 'secondary'
    }
    if (status === 'success' || status === 'sent') {
        return 'success'
    }
    return 'danger'
}

const resetFilters = () => {
    filterData.value.status = null
    filterData.value.mailStatus = null
    changePage()
}

const changePage = (page) => {
    const query = Object.fromEntries(Object.entries(filterData.value).filter(([_, v]) => v))
    if (page > 1) {
        query.page = page
    }

    router.get(route('contracts.pricing.preview', { pricingUpload: props.pricingUpload.id }), query)
}

const resendCustomerEmail = (item) => {
    router.patch(route('contracts.pricing.resend-customer-mail', { pricingUpload: props.pricingUpload.id, customer: item.customer.id }))
}

const openModal = async (value) => {
    errors = []
    displayData = null

    try {
        const response = await window.axios.post(route('contracts.pricing.preview-email'), {
            pricingUploadId: props.pricingUpload.id,
            customer: value.customer.id,
            contractId: value.contract.id
        })
        displayData = response.data
    } catch (error) {
        const errorObject = error.response && error.response.data && error.response.data.errors
            ? error.response.data.errors
            : {}
        Object.keys(errorObject).forEach((field) => {
            errorObject[field].forEach((message) => {
                errors.push(message)
            })
        })
    }
    showModal.value = true
}

const closeModal = () => (showModal.value = false)

function downloadDomainDecouplingCsv () {
    const link = document.createElement('a')
    link.href = route('contracts.pricing.download-domain-decoupling-csv', props.pricingUpload.id)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
}

</script>

<style scoped lang="scss">

</style>
